import * as React from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';

export interface SearchInputProps {
  /** Controlled value */
  value: string;
  /** Called on every keystroke */
  onChange: (value: string) => void;
  /** Input placeholder text. Required — no English fallback (also used as the aria-label). */
  placeholder: string;
  /** Whether to show the ⌘K keyboard shortcut hint (default true) */
  showKbd?: boolean;
  /** Optional className for the root wrapper */
  className?: string;
}

/**
 * SearchInput — glass-style text input with a leading magnifying glass icon
 * and an optional ⌘K pill hint on the right edge.
 *
 * Pressing Escape clears the value.
 *
 * @example
 * const [q, setQ] = useState('');
 * <SearchInput value={q} onChange={setQ} placeholder="Search quotes…" />
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    { value, onChange, placeholder, showKbd = true, className },
    ref,
  ) {
    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'Escape') onChange('');
    }

    const wrapperStyle: CSSProperties = {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    };

    const iconStyle: CSSProperties = {
      position: 'absolute',
      left: '0.625rem',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
      color: 'var(--ink-faint)',
      width: 16,
      height: 16,
      flexShrink: 0,
    };

    const inputStyle: CSSProperties = {
      background: 'rgba(255,255,255,0.65)',
      border: '1px solid rgba(15,23,42,0.10)',
      borderRadius: 8,
      padding: '0.55rem 0.75rem',
      paddingLeft: '2rem',
      paddingRight: showKbd ? '3.25rem' : '0.75rem',
      fontSize: 14,
      color: 'var(--ink)',
      boxShadow:
        '0 1px 0 rgba(255,255,255,0.85) inset, 0 1px 2px rgba(15,23,42,0.03)',
      transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
      width: '100%',
      outline: 'none',
    };

    const kbdStyle: CSSProperties = {
      position: 'absolute',
      right: '0.5rem',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.2rem',
      /* glass-pill */
      background: 'rgba(255,255,255,0.75)',
      backdropFilter: 'blur(24px) saturate(200%)',
      WebkitBackdropFilter: 'blur(24px) saturate(200%)',
      border: '1px solid rgba(255,255,255,0.85)',
      boxShadow:
        '0 1px 0 rgba(255,255,255,0.95) inset, 0 6px 16px -6px rgba(15,23,42,0.12)',
      borderRadius: '4px',
      padding: '0.125rem 0.375rem',
      fontSize: 11,
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontWeight: 500,
      color: 'var(--ink-faint)',
      whiteSpace: 'nowrap',
    };

    return (
      <div style={wrapperStyle} className={className}>
        {/* Magnifying glass icon */}
        <svg
          style={iconStyle}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>

        <input
          ref={ref}
          type="text"
          role="searchbox"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'rgba(31,58,95,0.5)';
            e.currentTarget.style.boxShadow =
              '0 0 0 3px rgba(31,58,95,0.12), 0 1px 0 rgba(255,255,255,0.85) inset';
            e.currentTarget.style.background = 'rgba(255,255,255,0.8)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)';
            e.currentTarget.style.boxShadow =
              '0 1px 0 rgba(255,255,255,0.85) inset, 0 1px 2px rgba(15,23,42,0.03)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.65)';
          }}
          aria-label={placeholder}
        />

        {showKbd && (
          <kbd style={kbdStyle} aria-hidden="true">
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            ⌘K
          </kbd>
        )}
      </div>
    );
  },
);
